import React from "react";
import {
    Dialog,
    DialogContent,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    InputLabel,
    Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { taskAssign } from "../../../config/images";
import { Button } from "../../../components/Button/button";
import translations from "../../../config/language";
import { styled } from "@mui/material/styles";
import { notifyError } from "../../../config/ToastMessage";

const EditTaskAssignPopup = ({
    open,
    onClose,
    taskData,
    setTaskData,
    selectedRegion,
    setSelectedRegion,
    selectedState,
    setSelectedState,
    selectedLocalGov,
    setSelectedLocalGov,
    selectedDataCollector,
    setSelectedDataCollector,
    regionData,
    stateData,
    localGovernmentData,
    dataCollectorData,
    handleUpdateTaskAssignment,
    loading,
    language
}) => {
    const NotificationSwitch = styled(Switch)(({ theme }) => ({
        padding: 8,
        width: 70,
        "& .MuiSwitch-track": {
            borderRadius: 22 / 2,
            width: "100%",
            "&::before, &::after": {
                content: '""',
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                width: 70,
                height: 16,
                fontSize: 12,
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
            "&::before": {
                content: `'${translations[language].off}'`,
                left: 8,
                color: "#FFFFFF",
                fontWeight: "bold",
            },
            "&::after": {
                content: `'${translations[language].on}'`,
                right: 12,
                color: theme.palette.text.secondary,
                fontWeight: "bold",
            },
        },
        "& .MuiSwitch-thumb": {
            boxShadow: "none",
            width: 16,
            height: 16,
            margin: 2,
            transform: "translateX(0px)",
        },
    }));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTaskData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSwitchChange = (e) => {
        setTaskData((prev) => ({
            ...prev,
            notification: e.target.checked,
        }));
    };

    const handleRegionChange = (e) => {
        const regionId = e.target.value;
        setSelectedRegion(regionId);
        // Reset state and local government when region changes
        setSelectedState("");
        setSelectedLocalGov("");
    };

    const validateForm = () => {
        // Validate task title
        if (!taskData.taskTitle) {
            notifyError(translations[language].pleaseEnterTaskTitle);
            return false;
        }
        // Validate task description
        if (!taskData.taskDescription) {
            notifyError(translations[language].pleaseEnterTaskDescription);
            return false;
        }
        // Validate region
        if (!selectedRegion) {
            notifyError(translations[language].pleaseSelectRegion);
            return false;
        }
        // Validate data collector
        if (!selectedDataCollector) {
            notifyError(translations[language].pleaseSelectDataCollector);
            return false;
        }
        // Validate number of farmers
        if (!taskData.numberOfFarmers || !Number.isInteger(Number(taskData.numberOfFarmers))) {
            notifyError(translations[language].numberOfFarmerCantBeDecimal);
            return false;
        }
        // Validate if number of farmers is zero
        if (taskData.numberOfFarmers == 0) {
            notifyError(translations[language].numberOfFarmerCantBeZero);
            return false;
        }
        // Validate deadline exists
        if (!taskData.deadline) {
            notifyError(translations[language].pleaseEnterDeadline);
            return false;
        }
        // Validate deadline is not in past
        if (taskData.deadline && dayjs(taskData.deadline).isBefore(dayjs(), 'day')) {
            notifyError(translations[language].deadlineCantBePastDate);
            return false;
        }
        // Validate deadline is not too far in future
        const maxDate = dayjs().add(5, 'year');
        if (taskData.deadline && dayjs(taskData.deadline).isAfter(maxDate)) {
            notifyError(translations[language].deadlineTooFarInFuture);
            return false;
        }

        return true;
    };


    return (
        <Dialog
            open={open}
            maxWidth="xs"
            fullWidth
            disableScrollLock
            className="dialog-assign"
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    margin: 2,
                    maxHeight: "calc(100% - 64px)",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)"
                },
            }}
        >
            <div className="assign-div">
                <div className="d-flex justify-content-between align-items-center p-3">
                    <h5 className="mb-0 flex-grow-1 text-center">
                        {translations[language].editAssignTask}
                    </h5>
                    <img
                        src={taskAssign.closeIcon}
                        alt=""
                        style={{
                            height: "30px",
                            width: "30px",
                            cursor: "pointer",
                        }}
                        onClick={onClose}
                    />
                </div>
            </div>
            <DialogContent>
                <TextField
                    fullWidth
                    label={translations[language].enterTaskTitle}
                    name="taskTitle"
                    value={taskData.taskTitle}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue.length <= 50) {
                            handleChange(e);
                        }
                    }}
                    className="mb-3"
                />
                <TextField
                    fullWidth
                    id="outlined-multiline-static"
                    label={translations[language].description}
                    name="taskDescription"
                    multiline
                    value={taskData.taskDescription}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue.length <= 150) {
                            handleChange(e);
                        }
                    }}
                    className="textarea-taskDescription mb-3"
                    rows={3}
                />
                <FormControl fullWidth className="mb-3">
                    <InputLabel>{translations[language].selectRegion}</InputLabel>
                    <Select
                        label={translations[language].selectRegion}
                        name="region"
                        value={selectedRegion}
                        onChange={handleRegionChange}
                    >
                        {regionData.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.region_name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth className="mb-3">
                    <InputLabel>{translations[language].selectState}</InputLabel>
                    <Select
                        label={translations[language].selectState}
                        name="state"
                        value={selectedState}
                        onChange={(e) => setSelectedState(e.target.value)}
                    >
                        {selectedRegion && stateData.length === 0 ? (
                            <MenuItem disabled>
                                {translations[language].noOptionAvailable}
                            </MenuItem>
                        ) : (
                            !selectedRegion && (
                                <MenuItem disabled>
                                    {translations[language].pleaseSelectRegion}
                                </MenuItem>
                            )
                        )}
                        {stateData.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.state_name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth className="mb-3">
                    <InputLabel>{translations[language].selectLocalGovernment}</InputLabel>
                    <Select
                        label={translations[language].selectLocalGovernment}
                        name="localGovernment"
                        value={selectedLocalGov}
                        onChange={(e) => setSelectedLocalGov(e.target.value)}
                    >
                        {selectedRegion && selectedState && localGovernmentData.length === 0 ? (
                            <MenuItem disabled>
                                {translations[language].noOptionAvailable}
                            </MenuItem>
                        ) : (
                            (!selectedRegion || !selectedState) && (
                                <MenuItem disabled>
                                    {translations[language].pleaseSelectState}
                                </MenuItem>
                            )
                        )}
                        {localGovernmentData.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.local_government_name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth className="mb-3">
                    <Autocomplete
                        name="dataCollector"
                        value={selectedDataCollector || null}
                        onChange={(e, newValue) => setSelectedDataCollector(newValue)}
                        options={dataCollectorData}
                        getOptionLabel={(option) =>
                            `${option.first_name} ${option.middle_name} ${option.last_name}`
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={translations[language].selectDataCollector}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />
                </FormControl>
                <TextField
                    fullWidth
                    label={translations[language].enterNumberOfFarmers}
                    name="numberOfFarmers"
                    type="number"
                    value={taskData.numberOfFarmers}
                    onChange={handleChange}
                    className="mb-3"
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={translations[language].deadline}
                        value={taskData.deadline}
                        onChange={(value) =>
                            setTaskData((prev) => ({ ...prev, deadline: value }))
                        }
                        className="mb-3"
                        minDate={dayjs().startOf("day")}
                    />
                </LocalizationProvider>
                <FormControlLabel
                    control={
                        <NotificationSwitch
                            checked={taskData.notification}
                            onChange={handleSwitchChange}
                            inputProps={{ "aria-label": "notification switch" }}
                        />
                    }
                    label={translations[language].notification}
                    className="mb-2 mt-2"
                    labelPlacement="start"
                    style={{ marginLeft: "0px" }}
                />
                <Button
                    title={translations[language].update}
                    classname={"assign-task-button"}
                    onClick={(e) => {
                        e.preventDefault();
                        if (validateForm()) {
                            handleUpdateTaskAssignment(e);
                        }
                    }}
                    loading={loading}
                />
            </DialogContent>
        </Dialog>
    );
};

export default EditTaskAssignPopup;